import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { Check, X } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { Issue } from '../../types';

interface EditIssueFormProps {
  issue: Issue;
  rockId: string;
  onUpdate: (issueId: string, name: string, description: string) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Inline form for editing an existing issue
 * Uses Formik for form state and Yup for validation
 */
export function EditIssueForm({
  issue,
  rockId,
  onUpdate,
  onSuccess,
  onCancel,
}: EditIssueFormProps) {
  const toast = useToast();

  // Color mode values for dark mode support
  const formBg = useColorModeValue('red.50', 'red.900');
  const inputBg = useColorModeValue('white', 'gray.700');

  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Issue name is required')
      .min(3, 'Issue name must be at least 3 characters'),
    description: Yup.string(),
  });

  const handleSubmit = async (
    values: { name: string; description: string },
    { setSubmitting }: any
  ) => {
    try {
      await onUpdate(issue.id, values.name, values.description);
      toast({
        title: 'Issue updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} bg={formBg} borderRadius="md" mb={2}>
      <Formik
        initialValues={{
          name: issue.name,
          description: issue.description || '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <HStack spacing={2} align="flex-start">
              <VStack spacing={0} pt={1}>
                {onCancel && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onCancel}
                    aria-label="Cancel"
                    isDisabled={isSubmitting}
                    mb={1}
                  >
                    <Icon as={X} w={4} h={4} />
                  </Button>
                )}
                <Button
                  type="submit"
                  size="sm"
                  variant="ghost"
                  colorScheme="green"
                  isLoading={isSubmitting}
                  aria-label="Save"
                >
                  <Icon as={Check} w={4} h={4} />
                </Button>
              </VStack>

              <VStack spacing={2} align="stretch" flex={1}>
                <Field name="name">
                  {({ field, form }: any) => (
                    <FormControl isInvalid={form.errors.name && form.touched.name}>
                      <Input
                        {...field}
                        placeholder="Issue name"
                        size="sm"
                        bg={inputBg}
                      />
                      <FormErrorMessage fontSize="xs">{form.errors.name}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Field name="description">
                  {({ field, form }: any) => (
                    <FormControl isInvalid={form.errors.description && form.touched.description}>
                      <Textarea
                        {...field}
                        placeholder="Issue description"
                        size="sm"
                        bg={inputBg}
                        rows={2}
                      />
                      <FormErrorMessage fontSize="xs">{form.errors.description}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </VStack>
            </HStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
